/**
 * Urgent Alert ↔ ASSIST cross-link resolution (#185).
 *
 * The PEOC is the single point of contact (doctrine §2.2): one incident can carry
 * BOTH an Urgent Alert (a fused hotspot / CAP warning the operator is working) and
 * an Assistance Request raised against it. Today those live on two separate
 * surfaces with no thread between them. This module is the pure join logic that
 * decides when a thread exists, so the UI can render a cross-link affordance ONLY
 * when one does (design-council PART IX — contextual, no COP clutter when absent).
 *
 * THE JOIN KEY. There is no `incident_id` FK on either side. An Urgent Alert
 * (queue source + raw-record id), an Assistance Request (`incident_link_type` +
 * `incident_link_id`), and a tracked Incident (`source_record_type` +
 * `source_record_id`) all reference the SAME raw source record. That shared source
 * record is the join. Two items are "connected to the same incident" when they
 * point at the same raw record.
 *
 * THE OVERLAP. The three surfaces speak three different source vocabularies; only
 * two source kinds are simultaneously (a) an Urgent-Alert queue source with a COP
 * detail panel AND (b) a valid Assistance-Request `incident_link_type`:
 *   • fused high-risk hotspot  — alert kind `highRiskHotspot` ↔ link `high_risk_hotspot`
 *   • CAP public-safety alert  — alert kind `capAlert`        ↔ link `cap_alert`
 * The other link types (active_fire, utility_outage, other) and the other queue
 * sources (smoke, ice, flood) have no counterpart on the opposite surface, so they
 * resolve to no link — which is exactly the contextual gate, not a special case.
 *
 * This is render-only matching: no new marker, no auto-urgent behaviour.
 */
import type { IncidentLinkType } from '@tsr/shared-types';
import type { BridgeSelection, BridgeSelectionKind } from '../selectionBridge';

/** The SidePanel selection kinds that are Urgent-Alert detail surfaces able to
 *  host a "Connected ASSIST" affordance. Mirrors `Selection['kind']` for the two
 *  cross-linkable sources. */
export type AlertSelectionKind = 'highRiskHotspot' | 'capAlert';

/** An assist's stored incident link — the pair is all-or-nothing in the schema. */
export interface AssistIncidentLink {
  incident_link_type: IncidentLinkType | null;
  incident_link_id: string | null;
  /** Optional centroid carried into the COP fly-to (the bridge can resolve its
   *  own for cap/hotspot, so these are best-effort). */
  lat?: number | null;
  lon?: number | null;
}

/** Map a hosting Urgent-Alert detail panel to the assist link type that points at
 *  the same raw record. Only the two cross-linkable kinds resolve. */
const ALERT_KIND_TO_LINK_TYPE: Record<AlertSelectionKind, IncidentLinkType> = {
  highRiskHotspot: 'high_risk_hotspot',
  capAlert: 'cap_alert',
};

/** Map an assist link type to the selection-bridge kind the COP flies to. Only the
 *  two cross-linkable types resolve; everything else returns null (no link). */
const LINK_TYPE_TO_BRIDGE_KIND: Partial<Record<IncidentLinkType, BridgeSelectionKind>> = {
  high_risk_hotspot: 'hotspot',
  cap_alert: 'cap',
};

/**
 * Direction B (Alert → ASSIST): the assist-list filter params that find every
 * request linked to the raw record behind an open Urgent-Alert panel.
 * `recordId` is the panel feature's id (== `incident_link_id` on a linked assist).
 * Returns null for an alert kind that has no assist counterpart — the caller skips
 * the lookup entirely (contextual gate).
 */
export function alertLinkLookup(
  kind: AlertSelectionKind,
  recordId: string | null | undefined,
): { incident_link_type: IncidentLinkType; incident_link_id: string } | null {
  if (!recordId) return null;
  const incident_link_type = ALERT_KIND_TO_LINK_TYPE[kind];
  if (!incident_link_type) return null;
  return { incident_link_type, incident_link_id: recordId };
}

/**
 * Direction A (ASSIST → Alert): resolve the selection the COP should open for the
 * Urgent Alert an assist is connected to, or null when no cross-linkable alert
 * exists (the contextual gate — render nothing). A link exists only when the
 * request carries a complete, cross-linkable `incident_link_*` pair.
 */
export function resolveConnectedAlertSelection(link: AssistIncidentLink): BridgeSelection | null {
  const { incident_link_type, incident_link_id } = link;
  if (!incident_link_type || !incident_link_id) return null;
  const kind = LINK_TYPE_TO_BRIDGE_KIND[incident_link_type];
  if (!kind) return null;
  const selection: BridgeSelection = { kind, id: incident_link_id };
  if (typeof link.lat === 'number' && Number.isFinite(link.lat)) selection.lat = link.lat;
  if (typeof link.lon === 'number' && Number.isFinite(link.lon)) selection.lon = link.lon;
  return selection;
}

/** Whether an assist has a cross-linkable Urgent Alert (the render gate for the
 *  "Connected Alert" affordance). Thin boolean wrapper over the resolver. */
export function hasConnectedAlert(link: AssistIncidentLink): boolean {
  return resolveConnectedAlertSelection(link) !== null;
}
